import { createFileRoute, redirect } from '@tanstack/react-router';
import { configRouteRedirect } from '~/lib/config-access';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Plus,
  Star,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
  Save,
  GitBranch,
} from 'lucide-react';
import { currentUser } from '~/lib/pb';
import { SortableList, SortableItem, DragHandle, arrayMove } from '~/components/SortableList/SortableList';
import {
  usePipelines,
  usePipelineStages,
  useCreatePipeline,
  useUpdatePipeline,
  useDeletePipeline,
  useCreateStage,
  useUpdateStage,
  useDeleteStage,
  useReorderStages,
} from '~/lib/queries';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import type { Pipeline, PipelineStage } from '~/lib/types';
import styles from './_authed.pipelines.module.css';

// /pipelines redirige a /settings?tab=pipelines — el contenido se rendea
// embebido como tab de Settings (refactor 2026-05-06: configuración del
// tenant consolidada). Mantenemos esta ruta para back-compat con bookmarks
// y links viejos.
export const Route = createFileRoute('/_authed/pipelines')({
  beforeLoad: () => {
    const dest = configRouteRedirect(currentUser()?.role, '/pipelines');
    if (dest) throw redirect(dest);
    throw redirect({ to: '/settings', search: { tab: 'pipelines' } });
  },
});

const STAGE_TYPE_LABEL: Record<'open' | 'won' | 'lost', string> = {
  open: 'En curso',
  won: 'Ganado',
  lost: 'Perdido',
};

const DEFAULT_STAGE_COLORS = [
  '#6b7280', '#3b82f6', '#8b5cf6', '#ec4899',
  '#f59e0b', '#10b981', '#ef4444', '#14b8a6',
];

/**
 * Vista de pipelines embebida en Settings. Sin wrapper de página ni h1
 * principal — el wrapper lo provee el tab panel de Settings.
 */
export function PipelinesView() {
  const me = currentUser();
  const allowed = me?.role === 'superadmin';
  const { data: pipelines } = usePipelines();
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!pipelines || pipelines.length === 0) {
      setActivePipelineId(null);
      return;
    }
    if (!activePipelineId || !pipelines.find((p) => p.id === activePipelineId)) {
      const def = pipelines.find((p) => p.is_default) ?? pipelines[0];
      setActivePipelineId(def?.id ?? null);
    }
  }, [pipelines, activePipelineId]);

  if (!allowed) {
    return (
      <div className={styles.notAllowed}>
        <p>Solo el superadmin gestiona pipelines.</p>
      </div>
    );
  }

  const activePipeline = pipelines?.find((p) => p.id === activePipelineId) ?? null;

  return (
    <div className={styles.embedded}>
      <div className={styles.embeddedHead}>
        <p className={styles.subtitle}>
          Cada pipeline es un flujo de venta del cliente con sus etapas. Las oportunidades van por uno solo a la vez.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setCreating(true)}
        >
          <Plus size={14} aria-hidden /> Nuevo pipeline
        </button>
      </div>

      {creating && (
        <NewPipelineForm
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setActivePipelineId(id);
            setCreating(false);
          }}
        />
      )}

      {(!pipelines || pipelines.length === 0) ? (
        <div className="empty-state">
          <GitBranch size={28} aria-hidden className="empty-state__icon" />
          <p>Aún no hay pipelines. Crea el primero con "Nuevo pipeline".</p>
        </div>
      ) : (
        <div className={styles.layout}>
          <nav className={styles.pipelineList}>
            {pipelines.map((p) => (
              <button
                key={p.id}
                type="button"
                className={
                  p.id === activePipelineId
                    ? `${styles.pipelineListItem} ${styles.pipelineListItem__active}`
                    : styles.pipelineListItem
                }
                onClick={() => setActivePipelineId(p.id)}
              >
                <span className={styles.pipelineListItem__name}>
                  {p.is_default && <Star size={11} aria-label="default" />}
                  {p.name}
                </span>
                {p.archived && <span className={styles.archivedBadge}>Archivado</span>}
              </button>
            ))}
          </nav>

          {activePipeline && (
            <PipelineDetail pipeline={activePipeline} />
          )}
        </div>
      )}
    </div>
  );
}

// ── New pipeline form ─────────────────────────────────────────────

function NewPipelineForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const create = useCreatePipeline();
  const createStage = useCreateStage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    try {
      const pipeline = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        is_default: isDefault,
      });
      // Crear stages default para que no quede vacío.
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        name: 'Nuevo',
        stage_type: 'open',
        color: '#3b82f6',
      });
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        name: 'En contacto',
        stage_type: 'open',
        color: '#8b5cf6',
      });
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        name: 'Propuesta',
        stage_type: 'open',
        color: '#f59e0b',
      });
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        name: 'Ganado',
        stage_type: 'won',
        color: '#10b981',
      });
      await createStage.mutateAsync({
        pipelineId: pipeline.id,
        name: 'Perdido',
        stage_type: 'lost',
        color: '#ef4444',
      });
      onCreated(pipeline.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear');
    }
  }

  return (
    <form className={styles.card} onSubmit={handleSubmit}>
      <div className={styles.cardHeader}>
        <h3>Nuevo pipeline</h3>
        <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Cancelar">
          <X size={16} />
        </button>
      </div>
      <div className={styles.cardBody}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Nombre *</span>
          <input
            type="text"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Ventas, Soporte, Renovaciones"
            autoFocus
            required
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Descripción (opcional)</span>
          <input
            type="text"
            className={styles.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>Marcar como pipeline por defecto del cliente</span>
        </label>
        <p className={styles.hint}>
          Se crearán 5 etapas iniciales (Nuevo · En contacto · Propuesta · Ganado · Perdido). Puedes editarlas después.
        </p>
        {error && <div className={styles.error}>{error}</div>}
      </div>
      <div className={styles.cardFooter}>
        <button type="button" className={styles.ghostBtn} onClick={onClose}>
          Cancelar
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={create.isPending}>
          {create.isPending ? 'Creando…' : 'Crear pipeline'}
        </button>
      </div>
    </form>
  );
}

// ── Pipeline detail ──────────────────────────────────────────────

function PipelineDetail({ pipeline }: { pipeline: Pipeline }) {
  const { data: stages } = usePipelineStages(pipeline.id);
  const update = useUpdatePipeline();
  const del = useDeletePipeline();
  const reorder = useReorderStages();
  const confirm = useConfirm();
  const [editingMeta, setEditingMeta] = useState(false);

  async function handleDelete() {
    const ok = await confirm({
      title: `Eliminar "${pipeline.name}"`,
      description: (
        <>
          Esto borra el pipeline y todas sus etapas. Los leads que apunten a este pipeline pueden quedar huérfanos. La acción es irreversible.
        </>
      ),
      confirmLabel: 'Eliminar pipeline',
      tone: 'danger',
    });
    if (!ok) return;
    del.mutate(pipeline.id);
  }


  return (
    <section className={styles.detail}>
      <div className={styles.detail__header}>
        {editingMeta ? (
          <PipelineMetaForm
            pipeline={pipeline}
            onSave={(patch) =>
              update.mutate(
                { pipelineId: pipeline.id, patch },
                { onSuccess: () => setEditingMeta(false) },
              )
            }
            onCancel={() => setEditingMeta(false)}
          />
        ) : (
          <>
            <div>
              <h2 className={styles.detail__title}>
                {pipeline.is_default && <Star size={14} aria-label="default" />}
                {pipeline.name}
              </h2>
              {pipeline.description && (
                <p className={styles.detail__desc}>{pipeline.description}</p>
              )}
              <div className={styles.detail__meta}>
                <code>slug: {pipeline.slug}</code>
                {pipeline.archived && <span className={styles.archivedBadge}>Archivado</span>}
              </div>
            </div>
            <div className={styles.detail__actions}>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => setEditingMeta(true)}
              >
                <Pencil size={14} aria-hidden /> Editar
              </button>
              <button type="button" className={styles.dangerBtn} onClick={handleDelete}>
                <Trash2 size={14} aria-hidden /> Eliminar
              </button>
            </div>
          </>
        )}
      </div>

      <StagesList
        pipeline={pipeline}
        stages={stages ?? []}
        commitOrder={(orderedIds) => reorder.mutate(orderedIds)}
      />
    </section>
  );
}

function PipelineMetaForm({
  pipeline,
  onSave,
  onCancel,
}: {
  pipeline: Pipeline;
  onSave: (patch: Partial<Pipeline>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(pipeline.name);
  const [description, setDescription] = useState(pipeline.description ?? '');
  const [isDefault, setIsDefault] = useState(pipeline.is_default);
  const [archived, setArchived] = useState(pipeline.archived);
  return (
    <form
      className={styles.metaForm}
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ name: name.trim(), description, is_default: isDefault, archived });
      }}
    >
      <input
        type="text"
        className={styles.input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        required
      />
      <input
        type="text"
        className={styles.input}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Descripción"
      />
      <div className={styles.metaForm__checks}>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>Pipeline por defecto</span>
        </label>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setArchived(e.target.checked)}
          />
          <span>Archivado (oculto del kanban y lista)</span>
        </label>
      </div>
      <div className={styles.metaForm__actions}>
        <button type="button" className={styles.ghostBtn} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className={styles.primaryBtn}>
          Guardar
        </button>
      </div>
    </form>
  );
}

// ── Stages list ──────────────────────────────────────────────────

function StagesList({
  pipeline,
  stages,
  commitOrder,
}: {
  pipeline: Pipeline;
  stages: PipelineStage[];
  commitOrder: (orderedIds: string[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <div className={styles.stages}>
      <div className={styles.stages__header}>
        <h3 className={styles.stages__title}>Etapas ({stages.length})</h3>
        {!adding && (
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => setAdding(true)}
          >
            <Plus size={14} aria-hidden /> Etapa
          </button>
        )}
      </div>
      {adding && (
        <NewStageForm
          pipelineId={pipeline.id}
          onClose={() => setAdding(false)}
        />
      )}
      <div className={styles.stagesList}>
        <SortableList
          ids={stages.map((s) => s.id)}
          onReorder={(from, to) => commitOrder(arrayMove(stages, from, to).map((s) => s.id))}
        >
          {stages.map((s) => (
            <SortableItem key={s.id} id={s.id}>
              <StageItem stage={s} allStages={stages} />
            </SortableItem>
          ))}
        </SortableList>
      </div>
      {stages.length === 0 && !adding && (
        <p className={styles.empty}>Sin etapas. Crea la primera con "+".</p>
      )}
    </div>
  );
}

function NewStageForm({
  pipelineId,
  onClose,
}: {
  pipelineId: string;
  onClose: () => void;
}) {
  const create = useCreateStage();
  const [name, setName] = useState('');
  const [stageType, setStageType] = useState<'open' | 'won' | 'lost'>('open');
  const [color, setColor] = useState(DEFAULT_STAGE_COLORS[0]!);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { pipelineId, name: name.trim(), stage_type: stageType, color },
      { onSuccess: onClose },
    );
  }

  return (
    <form className={styles.stageForm} onSubmit={handleSubmit}>
      <input
        type="text"
        className={styles.input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nombre de la etapa"
        autoFocus
        required
      />
      <select
        className={styles.input}
        value={stageType}
        onChange={(e) => setStageType(e.target.value as 'open' | 'won' | 'lost')}
      >
        <option value="open">En curso</option>
        <option value="won">Ganado (cierre exitoso)</option>
        <option value="lost">Perdido (cierre fallido)</option>
      </select>
      <ColorPicker value={color} onChange={setColor} />
      <div className={styles.stageForm__actions}>
        <button type="button" className={styles.ghostBtn} onClick={onClose}>
          Cancelar
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={create.isPending}>
          {create.isPending ? 'Creando…' : 'Crear'}
        </button>
      </div>
    </form>
  );
}

function StageItem({
  stage,
  allStages,
}: {
  stage: PipelineStage;
  allStages: PipelineStage[];
}) {
  const update = useUpdateStage();
  const del = useDeleteStage();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);

  // Garantía: todo pipeline mantiene al menos un stage `won` y uno `lost`. Si
  // este stage es el único de su tipo, NO se puede borrar (rompería la
  // metáfora del embudo y los reportes downstream).
  const isOnlyWon =
    stage.stage_type === 'won' &&
    allStages.filter((s) => s.stage_type === 'won').length === 1;
  const isOnlyLost =
    stage.stage_type === 'lost' &&
    allStages.filter((s) => s.stage_type === 'lost').length === 1;
  const isProtected = isOnlyWon || isOnlyLost;

  async function handleDelete() {
    if (isProtected) {
      await confirm({
        title: `No puedes eliminar "${stage.name}"`,
        description: (
          <>
            Cada pipeline necesita al menos un stage de cierre exitoso (Ganado)
            y uno de cierre fallido (Perdido) — esto sostiene la metáfora del
            embudo y los reportes de conversión. Si quieres cambiar el nombre,
            usa el botón <strong>Editar</strong>. Si quieres reemplazarlo, crea
            primero otro stage del mismo tipo.
          </>
        ),
        confirmLabel: 'Entendido',
        tone: 'info',
        hideCancel: true,
      });
      return;
    }
    const ok = await confirm({
      title: `Eliminar etapa "${stage.name}"`,
      description: (
        <>
          Los leads que estén en esta etapa quedan huérfanos hasta que los muevas a otra. Esta acción no se puede deshacer.
        </>
      ),
      confirmLabel: 'Eliminar etapa',
      tone: 'danger',
    });
    if (ok) del.mutate(stage.id);
  }

  if (editing) {
    return (
      <div className={styles.stageItem}>
        <EditStageForm
          stage={stage}
          isOnlyWon={isOnlyWon}
          isOnlyLost={isOnlyLost}
          onSave={(patch) =>
            update.mutate(
              { stageId: stage.id, patch },
              { onSuccess: () => setEditing(false) },
            )
          }
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.stageItem}>
      <DragHandle />
      <span
        className={styles.stageItem__dot}
        style={{ background: stage.color || 'var(--color-text-subtle)' }}
        aria-hidden
      />
      <div className={styles.stageItem__main}>
        <span className={styles.stageItem__name}>{stage.name}</span>
        <span className={styles.stageItem__type}>
          {stage.stage_type === 'won' && <CheckCircle2 size={12} aria-hidden />}
          {stage.stage_type === 'lost' && <XCircle size={12} aria-hidden />}
          {STAGE_TYPE_LABEL[stage.stage_type]}
        </span>
        {stage.archived && <span className={styles.archivedBadge}>Archivada</span>}
      </div>
      <div className={styles.stageItem__actions}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => setEditing(true)}
          aria-label="Editar"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={handleDelete}
          aria-label="Eliminar"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function EditStageForm({
  stage,
  isOnlyWon,
  isOnlyLost,
  onSave,
  onCancel,
}: {
  stage: PipelineStage;
  /** True si este es el único stage `won` del pipeline (no se puede cambiar tipo). */
  isOnlyWon: boolean;
  /** True si este es el único stage `lost` del pipeline. */
  isOnlyLost: boolean;
  onSave: (patch: Partial<PipelineStage>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(stage.name);
  const [stageType, setStageType] = useState<'open' | 'won' | 'lost'>(stage.stage_type);
  const [color, setColor] = useState(stage.color ?? DEFAULT_STAGE_COLORS[0]!);
  const [archived, setArchived] = useState(stage.archived);

  // Si este es el único stage won/lost, su tipo no puede cambiarse — sería
  // dejar al pipeline sin el otro extremo del embudo. El nombre y color sí
  // se pueden editar libremente.
  const typeLocked = isOnlyWon || isOnlyLost;

  return (
    <form
      className={styles.stageForm}
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ name: name.trim(), stage_type: stageType, color, archived });
      }}
    >
      <input
        type="text"
        className={styles.input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        required
      />
      <select
        className={styles.input}
        value={stageType}
        onChange={(e) => setStageType(e.target.value as 'open' | 'won' | 'lost')}
        disabled={typeLocked}
        title={
          typeLocked
            ? `No puedes cambiar el tipo: este es el único stage ${
                isOnlyWon ? 'Ganado' : 'Perdido'
              } del pipeline. Renombralo si quieres.`
            : undefined
        }
      >
        <option value="open">En curso</option>
        <option value="won">Ganado</option>
        <option value="lost">Perdido</option>
      </select>
      <ColorPicker value={color} onChange={setColor} />
      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={archived}
          onChange={(e) => setArchived(e.target.checked)}
        />
        <span>Archivada</span>
      </label>
      <div className={styles.stageForm__actions}>
        <button type="button" className={styles.ghostBtn} onClick={onCancel}>
          <XCircle size={14} aria-hidden /> Cancelar
        </button>
        <button type="submit" className={styles.primaryBtn}>
          <Save size={14} aria-hidden /> Guardar
        </button>
      </div>
    </form>
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className={styles.colorPicker} role="radiogroup" aria-label="Color de la etapa">
      {DEFAULT_STAGE_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={
            c === value
              ? `${styles.colorSwatch} ${styles.colorSwatch__active}`
              : styles.colorSwatch
          }
          style={{ background: c }}
          onClick={() => onChange(c)}
          aria-label={`Color ${c}`}
        />
      ))}
    </div>
  );
}
